// Lista usług konkretnej pozycji renderowana w sekcji "Upsale" rozwiniętego wiersza
// pozycji oferty (`TraderClientOfferProductsContainer`). Każdy wpis ma akcje
// Edytuj / Usuń (tylko w stanie DRAFT). Dodawanie odbywa się przez link
// "DODAJ USŁUGĘ" w karcie produktu - ten komponent NIE renderuje przycisku "Dodaj".

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { WiseModal } from 'components/wise';
import {
  useDeleteSalesCrmOfferService,
  useGetSalesCrmOfferServices,
  type ISalesCrmOfferService
} from 'api';

import AddOfferServiceModal from '../../TraderClientOfferServicesContainer/AddOfferServiceModal';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/TraderClientOfferProductsContainer.module.scss';

const formatPrice = (raw?: number | null): string => {
  if (raw == null || Number.isNaN(raw)) return '0,00 zł';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

interface IPositionUpsaleServicesListProps {
  clientId: number;
  offerId: string;
  positionId: number;
  /** Edytuj/Usuń dostępne tylko gdy oferta jest w stanie DRAFT. */
  isOfferDraft: boolean;
  /** Czy nad listą usług ma być wyświetlony separator (true gdy są też produkty upsell). */
  showDivider?: boolean;
}

const PositionUpsaleServicesList: FC<IPositionUpsaleServicesListProps> = ({
  clientId,
  offerId,
  positionId,
  isOfferDraft,
  showDivider = false
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [editingService, setEditingService] = useState<ISalesCrmOfferService | null>(null);
  const [deletingService, setDeletingService] = useState<ISalesCrmOfferService | null>(null);

  const { data, isLoading } = useGetSalesCrmOfferServices(clientId, offerId, positionId);
  const items = data?.items ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries([
      'sales-crm',
      'client',
      clientId,
      'offer',
      offerId,
      'position',
      positionId,
      'services'
    ]);
    queryClient.invalidateQueries([
      'sales-crm',
      'client',
      clientId,
      'offer',
      offerId,
      'services-all'
    ]);
    queryClient.invalidateQueries([
      'sales-crm',
      'client',
      clientId,
      'offer',
      offerId,
      'totals'
    ]);
    queryClient.invalidateQueries([
      'sales-crm',
      'client',
      clientId,
      'offer',
      offerId,
      'version'
    ]);
  };

  const { mutate: deleteService, isLoading: isDeleting } = useDeleteSalesCrmOfferService(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        invalidate();
        setDeletingService(null);
      }
    }
  );

  if (isLoading || items.length === 0) {
    // Loading: nie pokazujemy nic - lista usług jest opcjonalnym dodatkiem do Upsale.
    // Brak usług: w ogóle nie renderujemy sekcji żeby nie wprowadzać szumu.
    return null;
  }

  return (
    <React.Fragment>
      {showDivider && <div className={styles.upsaleDivider} />}
      <div className={styles.upsaleList}>
        {items.map((service, idx) => (
          <div
            key={service.id}
            className={
              idx === 0 ? styles.upsaleItem : `${styles.upsaleItem} ${styles.upsaleItemDivider}`
            }>
            <div className={styles.upsaleServiceBody}>
              <span className={styles.upsaleName}>{service.name}</span>
              <div className={styles.upsaleServicePrices}>
                <span>
                  {t('Netto')}: {formatPrice(service.priceNet)}
                </span>
                <span className={styles.upsaleServicePriceSeparator}>·</span>
                <span>
                  {t('Brutto')}: {formatPrice(service.priceGross)}
                </span>
                <span className={styles.upsaleServicePriceSeparator}>·</span>
                <span>
                  {t('VAT')}: {service.taxPercent}%
                </span>
                <span className={styles.upsaleServicePriceSeparator}>·</span>
                <span>
                  {service.priceType === 'UNIT' ? t('Cena jednostkowa') : t('Cena całościowa')}
                </span>
              </div>
            </div>
            {isOfferDraft && (
              <div className={styles.upsaleServiceActions}>
                <Tooltip title={t('Edytuj usługę')}>
                  <IconButton
                    size="small"
                    onClick={() => setEditingService(service)}
                    aria-label={t('Edytuj usługę')}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Usuń usługę')}>
                  <IconButton
                    size="small"
                    onClick={() => setDeletingService(service)}
                    aria-label={t('Usuń usługę')}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal: edycja istniejącej usługi pozycji */}
      <AddOfferServiceModal
        open={editingService !== null}
        mode="edit"
        clientId={clientId}
        offerId={offerId}
        positionId={positionId}
        editingService={editingService}
        onClose={() => setEditingService(null)}
        onSaved={invalidate}
      />

      {/* Confirm: usuwanie usługi */}
      {deletingService !== null && (
        <WiseModal
          open
          onClose={() => setDeletingService(null)}
          title={t('Usunąć usługę?')}
          maxWidth={420}>
          <div className={styles.deleteConfirmBody}>
            <p className={styles.deleteMessage}>
              {t('Czy na pewno chcesz usunąć tę usługę z pozycji?')}
            </p>
            <div className={styles.deleteName}>{deletingService.name}</div>
            <div className={styles.deleteActions}>
              <Button
                onClick={() => setDeletingService(null)}
                variant="text"
                disabled={isDeleting}>
                {t('Anuluj')}
              </Button>
              <Button
                onClick={() => deleteService(deletingService.id)}
                variant="contained"
                color="error"
                disabled={isDeleting}>
                {isDeleting ? <CircularProgress size={18} /> : t('Usuń')}
              </Button>
            </div>
          </div>
        </WiseModal>
      )}
    </React.Fragment>
  );
};

export default PositionUpsaleServicesList;
